import React from 'react';
import
{
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';

import
{
  LoginPage,
  SignupPage,
  ForgotPasswordPage,
  VerifyEmailPage,
  ChatPage,
  FedRampChatPage,
} from '@ui-pages';
import Dashboard from './components/Dashboard';
import ArtifactViewer from './components/ArtifactViewer';
import DesignViewer from './components/DesignViewer';
import FormViewer from './components/FormViewer';
import CABViewer from './components/CABViewer';
import {
  NotFoundErrorPage,
  AccessForbiddenPage,
} from './components/ErrorPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { getRouterBase } from './utils/basePath';
import { setNavigateFunction, setLogoutFunction, setRefreshTokenFunction } from './utils/axios';
import { buildLoginUrlWithRedirect, getRedirectToFromQuery, getDefaultRedirectPath } from './utils/redirectUtils';

// Admin components (detachable module)
import AdminRoute from './admin/components/AdminRoute';
import AdminDashboard from './admin/components/AdminDashboard';
import UserDetailView from './admin/components/UserDetailView';
import { isAdminUser } from './admin/utils';

//custom styles
import './styles/index.css';

/**
 * Private route component that redirects unauthenticated users to login
 * with the current path appended as redirectTo query parameter
 */
function PrivateRoute({ children }: { children: JSX.Element }) {
  const { isAuthenticated, isAdminOnly } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // Build current path including search and hash
    const currentPath = location.pathname + location.search + (location.hash || '');
    const loginUrl = buildLoginUrlWithRedirect(currentPath);
    return <Navigate to={loginUrl} replace />;
  }

  // Redirect admin-only users to admin dashboard only from dashboard route
  if (isAdminOnly && location.pathname === '/dashboard') {
    return <Navigate to="/admin" replace />;
  }

  return children;
}

/**
 * Smart redirect component for root and catch-all routes
 * Handles redirect logic for unauthenticated users
 */
function SmartRedirect() {
  const { isAuthenticated, user, activeRole } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // Build current path and redirect to login with redirectTo query param
    const currentPath = location.pathname + location.search + (location.hash || '');
    const loginUrl = buildLoginUrlWithRedirect(currentPath);
    return <Navigate to={loginUrl} replace />;
  }

  // Wait for user data to be loaded
  if (!user) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>;
  }

  // Check for redirectTo in query params with validation
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = getRedirectToFromQuery(searchParams, isAdminUser(user));
  
  if (redirectTo) {
    // Redirect to the validated path from query param
    return <Navigate to={redirectTo} replace />;
  }

  // Otherwise, redirect based on active role
  const defaultPath = getDefaultRedirectPath(activeRole, isAdminUser(user));
  return <Navigate to={defaultPath} replace />;
}

function AppRoutes() {
  const { isAuthenticated, logout, refreshAccessToken, user } = useAuth();
  const navigate = useNavigate();

  // Set the navigation, logout, and refresh token functions for axios interceptor
  React.useEffect(() => {
    setNavigateFunction(navigate);
    setLogoutFunction(logout);
    setRefreshTokenFunction(refreshAccessToken);
  }, [navigate, logout, refreshAccessToken]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={isAuthenticated ? <SmartRedirect /> : <LoginPage />} />
      <Route path="/signup" element={isAuthenticated ? <SmartRedirect /> : <SignupPage />} />
      <Route path="/forgot-password" element={isAuthenticated ? <SmartRedirect /> : <ForgotPasswordPage />} />
      <Route path="/verify-email" element={isAuthenticated ? <SmartRedirect /> : <VerifyEmailPage />} />
      
      {/* Admin routes (detachable module) */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/users/:userIdentifier" element={<AdminRoute><UserDetailView /></AdminRoute>} />
      {/* Future admin routes can be added here */}

      {/* Regular user routes */}
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/chat/:projectId" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
      <Route path="/chat/:projectId/:type" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
      <Route path="/chat/:projectId/:type/:threadId" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
      <Route path="/fedramp/chat/:projectId" element={<PrivateRoute><FedRampChatPage /></PrivateRoute>} />
      <Route path="/fedramp/chat/:projectId/:type" element={<PrivateRoute><FedRampChatPage /></PrivateRoute>} />
      <Route path="/project/:projectId/artifact/:artifactId" element={<ArtifactViewer />} />
      <Route path="/project/:projectId/design" element={<PrivateRoute><DesignViewer /></PrivateRoute>} />
      <Route path="/project/:projectId/form" element={<PrivateRoute><FormViewer /></PrivateRoute>} />
      <Route path="/project/:projectId/cab" element={<CABViewer />} />
      <Route path="/*" element={<NotFoundErrorPage />} />
      <Route path="/access-forbidden" element={<AccessForbiddenPage />} />

      {/* Root redirect with smart routing */}
      <Route path="/" element={<SmartRedirect />} />

      {/* Catch all route */}
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router basename={getRouterBase()}>
          <div className="min-h-screen">
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 